import { useEffect, useState } from 'react'

import { api, fehlertext } from '../../api/client'
import { datumText } from '../../lib/ortszeit'
import { eigenerEndpunktHash } from '../../lib/push'
import { useTexte } from '../../texte'

interface Geraet {
  endpoint_hash: string
  kind: string
  created_at: string
  delivered_at: string
  app_seen_at: string
  expires_at: string
}

interface Liste {
  expire_days: number
  devices: Geraet[]
}

/**
 * The devices of the own account and when each one expires (#121).
 *
 * The reason it is shown at all: a subscription of an app removed from the
 * home screen looked healthy for ever, and nobody could see it. Now it goes
 * after `expire_days` without the app reporting, and this list says when.
 * The own device is marked by the hash of its endpoint -- the server never
 * hands out an endpoint. `stand` changes whenever the card above switched
 * something, so the list follows.
 */
export function PushGeraete({ stand }: { stand: unknown }) {
  const t = useTexte()
  const [liste, setListe] = useState<Liste | null>(null)
  const [eigenes, setEigenes] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  async function laden() {
    try {
      setListe(await api.get<Liste>('/api/push/subscriptions'))
      setEigenes(await eigenerEndpunktHash())
    } catch (fehler) {
      setProblem(fehlertext(fehler))
    }
  }

  useEffect(() => {
    void laden()
  }, [stand])

  async function entfernen(hash: string) {
    setBusy(true)
    setProblem(null)
    try {
      await api.delete('/api/push/subscriptions', { endpoint_hash: hash })
      await laden()
    } catch (fehler) {
      setProblem(fehlertext(fehler))
    } finally {
      setBusy(false)
    }
  }

  if (liste === null || liste.devices.length === 0) {
    return problem === null ? null : <p className="notice" role="alert">{problem}</p>
  }

  return (
    <div className="stack--eintrag" data-schluessel="push-geraete">
      <p className="fett row--oben">{t.push.geraeteTitel}</p>
      <ul className="push-geraete">
        {liste.devices.map((geraet) => {
          const art = t.push.geraetArt[geraet.kind] ?? t.push.geraetArt.other ?? geraet.kind
          const meins = geraet.endpoint_hash === eigenes
          return (
            <li key={geraet.endpoint_hash} className="push-geraete__zeile" data-geraet={geraet.kind} data-eigenes={meins ? '' : undefined}>
              <span>
                <span className="fett">{art}</span>
                {meins && <span className="leise"> · {t.push.diesesGeraet}</span>}
                <br />
                <span className="feld__hinweis klein">{t.push.geraetZuletzt(datumText(geraet.app_seen_at))}</span>
                <br />
                <span className="feld__hinweis klein">{t.push.geraetBis(datumText(geraet.expires_at))}</span>
              </span>
              {!meins && (
                <button
                  className="button button--outline button--klein"
                  type="button"
                  disabled={busy}
                  aria-label={t.push.geraetEntfernenName(art)}
                  onClick={() => void entfernen(geraet.endpoint_hash)}
                >
                  {t.push.geraetEntfernen}
                </button>
              )}
            </li>
          )
        })}
      </ul>
      <p className="feld__hinweis">{t.push.geraeteFrist(Math.round(liste.expire_days / 7))}</p>
      {problem && <p className="notice" role="alert">{problem}</p>}
    </div>
  )
}
